import { useEffect, useRef } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@c2fo/liquidity';
import { GlobeAsiaIcon } from '@c2fo/liquidity/icons';
import { useDataRegionSelector } from '../../contexts/FileDeliveryContext';
import { DATACENTERS } from '@admin-ui-root/src/app/utils/settings';
import { useUserDefaults } from '../../hooks';
import { useErrorState } from '../ErrorBanner';

export const DataRegionSelect: React.FC = () => {
  const { dataRegion, setDataRegion, markInitialized } = useDataRegionSelector();
  const { defaults, isLoaded } = useUserDefaults();
  const { addError } = useErrorState();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!isLoaded || hasInitialized.current) return;

    if (dataRegion) {
      hasInitialized.current = true;
      markInitialized();
      return;
    }

    hasInitialized.current = true;

    const userDefault = defaults.defaultDataRegion;
    if (userDefault) {
      const isValid = DATACENTERS.some((dc) => dc.code === userDefault);
      if (isValid) {
        setDataRegion(userDefault);
        markInitialized();
        return;
      }

      addError({
        id: 'default-data-region-warning',
        message: `Your saved default data region "${userDefault}" is no longer available. Using system default.`,
        type: 'warning',
      });
    }

    if (DATACENTERS.length > 0) {
      setDataRegion(DATACENTERS[0].code);
    }

    markInitialized();
  }, [isLoaded, dataRegion, defaults.defaultDataRegion, setDataRegion, addError, markInitialized]);

  return (
    <Select value={dataRegion} onValueChange={setDataRegion}>
      <SelectTrigger size="sm" className="w-44">
        <span className="flex items-center gap-2">
          <GlobeAsiaIcon className="h-4 w-4 shrink-0" />
          <SelectValue placeholder="Select region" />
        </span>
      </SelectTrigger>
      <SelectContent>
        {DATACENTERS.map((dc) => (
          <SelectItem key={dc.code} value={dc.code}>
            {dc.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
